import {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type {
    StudentItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    PageContainer,
    StateCard,
    TextInput,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    RefreshCcwIcon,
    PencilIcon,
    TrashIcon,
    UsersIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

const Students = () => {
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        students,
        setStudents
    ] = useState<StudentItem[]>([]);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState<boolean>(false);

    const [
        modalVisible,
        setModalVisible
    ] = useState(false);

    const [
        editId,
        setEditId
    ] = useState<string | null>(null);

    const [
        formData,
        setFormData
    ] = useState({
        parent_name_surname: "",
        parent_address: "",
        reading_level: "",
        name_surname: "",
        parent_email: "",
        parent_phone: "",
        school_name: "",
        grade_year: "",
        address: "",
        email: "",
        notes: "",
        phone: "",
        tc_no: "",
        age: ""
    });

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const [
        searchQuery,
        setSearchQuery
    ] = useState("");

    const [
        debouncedSearch,
        setDebouncedSearch
    ] = useState("");

    const [
        previewItem,
        setPreviewItem
    ] = useState<StudentItem | null>(null);

    const [
        pagination,
        setPagination
    ] = useState({
        totalRecords: 0,
        currentPage: 1,
        totalPages: 1,
        limit: 10
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPagination(prev => ({
                ...prev,
                currentPage: 1
            }));
        }, 500);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchStudents = useCallback(async (page: number = 1) => {
        try {
            setIsLoading(true);
            const token = await AsyncStorage.getItem("authToken");
            const url = new URL(`${process.env.API_URL}/students`);
            url.searchParams.append("page", page.toString());
            url.searchParams.append("limit", pagination.limit.toString());

            if (debouncedSearch) {
                url.searchParams.append("search", debouncedSearch);
            }

            if (activeOrganizationId && user?.role === "root") {
                url.searchParams.append("organization_id", activeOrganizationId);
            }

            const response = await fetch(url.toString(), {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setStudents(result.data);
                if (result.meta) {
                    setPagination(result.meta);
                }
            }
        } catch (error) {
            console.error("Öğrenciler alınamadı:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        activeOrganizationId,
        debouncedSearch,
        user?.role,
        pagination.limit
    ]);

    useEffect(() => {
        if (user?.role === "root" && !activeOrganizationId) {
            setStudents([]);
            setPagination({
                totalRecords: 0,
                currentPage: 1,
                totalPages: 1,
                limit: 10
            });
            return;
        }

        fetchStudents(pagination.currentPage);
    }, [
        activeOrganizationId,
        fetchStudents,
        pagination.currentPage,
        user?.role
    ]);

    const handleSave = async () => {
        if (!formData.name_surname) {
            NCoreUIKitToast.open({
                title: localize("students-alert-name-required"),
                type: "danger"
            });
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = editId
                ? `${process.env.API_URL}/students/${editId}`
                : `${process.env.API_URL}/students`;

            const response = await fetch(url, {
                body: JSON.stringify({
                    ...formData,
                    ...(activeOrganizationId && user?.role === "root" ? {
                        organization_id: activeOrganizationId
                    } : {})
                }),
                headers: {
                    "Content-Type": "application/json",
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                },
                method: editId ? "PUT" : "POST"
            });

            const result = await response.json();
            if (result.status === "success") {
                setModalVisible(false);
                setEditId(null);
                setFormData({
                    parent_name_surname: "",
                    parent_address: "",
                    reading_level: "",
                    name_surname: "",
                    parent_email: "",
                    parent_phone: "",
                    school_name: "",
                    grade_year: "",
                    address: "",
                    email: "",
                    notes: "",
                    phone: "",
                    tc_no: "",
                    age: ""
                });
                fetchStudents(1);
            } else {
                NCoreUIKitToast.open({
                    title: result.message || localize("students-toast-unknown-error"),
                    type: "danger"
                });
            }
        } catch (error) {
            console.error("Kayıt hatası:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (id: string, name: string) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const response = await fetch(`${process.env.API_URL}/students/${id}`, {
                            headers: {
                                ...(activeOrganizationId && user?.role === "root" ? {
                                    "x-organization-id": activeOrganizationId
                                } : {}),
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });

                        const result = await response.json();
                        if (result.status === "success") {
                            fetchStudents(pagination.currentPage);
                        } else {
                            NCoreUIKitToast.open({
                                title: result.message || localize("students-toast-delete-failed"),
                                type: "danger"
                            });
                        }
                    } catch (error) {
                        console.error("Silme hatası:", error);
                    } finally {
                        isDeleting = false;

                        closeAnimation();
                    }
                },
                title: localize("students-dialog-delete-primary"),
                type: "danger"
            },
            secondaryButtonProps: {
                title: localize("students-dialog-delete-secondary"),
                onPress: ({
                    closeAnimation
                }) => closeAnimation(),
                type: "neutral"
            },
            content: localize("students-dialog-delete-content", [
                name
            ]),
            title: localize("students-dialog-delete-title"),
            variant: "yes-no",
            isVisible: true
        });
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                currentPage: newPage
            }));
        }
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerRow,
                    {
                        alignItems: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("students-page-title")}
                </Text>
                <View
                    style={{
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingMd
                    }}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchStudents(pagination.currentPage);
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="neutral"
                    />
                    <Button
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("students-page-add-button")}
                        onPress={() => {
                            setEditId(null);
                            setFormData({
                                parent_name_surname: "",
                                parent_address: "",
                                reading_level: "",
                                name_surname: "",
                                parent_email: "",
                                parent_phone: "",
                                school_name: "",
                                grade_year: "",
                                address: "",
                                email: "",
                                notes: "",
                                phone: "",
                                tc_no: "",
                                age: ""
                            });
                            setModalVisible(true);
                        }}
                        type="primary"
                    />
                </View>
            </View>
            <TextInput
                placeholder={localize("students-search-placeholder")}
                title={localize("students-search-placeholder")}
                style={{
                    marginBottom: spaces.spacingXl
                }}
                onChangeText={setSearchQuery}
                spreadBehaviour="stretch"
                value={searchQuery}
            />
            {
                isLoading && students.length === 0 ?
                    <ActivityIndicator
                        color={colors.content.icon.default}
                        size="large"
                    />
                :
                    students.length === 0 ? (
                        <StateCard
                            subTitle={localize("students-list-empty-subtitle")}
                            title={localize("students-list-empty-title")}
                        />) : (
                            <ScrollView
                                contentContainerStyle={[
                                    stylesheet.listContent,
                                    {
                                        paddingBottom: spaces.spacingXl,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                                showsVerticalScrollIndicator={false}
                            >
                                {students.map((item) => <TouchableOpacity
                                    style={[
                                        stylesheet.itemContainer,
                                        {
                                            backgroundColor: colors.content.container.mid,
                                            alignItems: isMobile ? "stretch" : "center",
                                            flexDirection: isMobile ? "column" : "row",
                                            borderRadius: radiuses.md,
                                            padding: spaces.spacingMd,
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                    onPress={() => setPreviewItem(item)}
                                    activeOpacity={0.7}
                                    key={item.id}
                                >
                                    <View
                                        style={[
                                            stylesheet.itemContent,
                                            {
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <View
                                            style={[
                                                stylesheet.itemIcon,
                                                {
                                                    backgroundColor: colors.content.container.default,
                                                    borderRadius: radiuses.full
                                                }
                                            ]}
                                        >
                                            <UsersIcon
                                                color={colors.content.icon.emphasized}
                                                size={24}
                                            />
                                        </View>
                                        <View
                                            style={[
                                                stylesheet.itemInfo,
                                                {
                                                    gap: spaces.spacingSm
                                                }
                                            ]}
                                        >
                                            <Text
                                                variant="titleMediumSize"
                                            >
                                                {item.name_surname}
                                            </Text>
                                            {(item.grade_year || item.phone) && (
                                                <Text
                                                    variant="bodyMediumSize"
                                                    color="mid"
                                                >
                                                    {[
                                                        item.grade_year,
                                                        item.phone
                                                    ].filter(Boolean).join(" • ")}
                                                </Text>)}
                                            {item.parent_name_surname && (
                                                <Text
                                                    variant="bodySmallSize"
                                                    color="low"
                                                >
                                                    {item.parent_phone ? localize("students-list-parent-label", [
                                                        item.parent_name_surname,
                                                        item.parent_phone
                                                    ]) : localize("students-list-parent-label-no-phone", [
                                                        item.parent_name_surname
                                                    ])}
                                                </Text>)}
                                        </View>
                                    </View>
                                    <View
                                        style={[
                                            stylesheet.itemActions,
                                            {
                                                alignItems: isMobile ? "stretch" : "center",
                                                flexDirection: isMobile ? "column" : "row",
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <Button
                                            icon={({
                                                color,
                                                size
                                            }) => <PencilIcon
                                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                size={size}
                                            />
                                            }
                                            onPress={() => {
                                                setEditId(item.id);
                                                setFormData({
                                                    parent_name_surname: item.parent_name_surname || "",
                                                    parent_address: item.parent_address || "",
                                                    reading_level: item.reading_level || "",
                                                    name_surname: item.name_surname || "",
                                                    parent_email: item.parent_email || "",
                                                    parent_phone: item.parent_phone || "",
                                                    school_name: item.school_name || "",
                                                    grade_year: item.grade_year || "",
                                                    address: item.address || "",
                                                    email: item.email || "",
                                                    notes: item.notes || "",
                                                    phone: item.phone || "",
                                                    tc_no: item.tc_no || "",
                                                    age: item.age || ""
                                                });
                                                setModalVisible(true);
                                            }}
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            title={localize("students-list-edit-button")}
                                            type="neutral"
                                        />
                                        <Button
                                            icon={({
                                                color,
                                                size
                                            }) => <TrashIcon
                                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                size={size}
                                            />
                                            }
                                            onPress={() => handleDelete(item.id, item.name_surname)}
                                            title={localize("students-list-delete-button")}
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            type="danger"
                                        />
                                    </View>
                                </TouchableOpacity>)}
                            </ScrollView>)
            }
            <Modal
                onOverlayPress={({
                    closeAnimation
                }) => closeAnimation(() => setPreviewItem(null))}
                onClosed={() => setPreviewItem(null)}
                id="student-preview-modal"
                portalName="modal-system"
                isActive={!!previewItem}
            >
                <View
                    style={[
                        stylesheet.modalContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: isMobile ? "100%" : 500,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingXl
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {localize("students-preview-title")}
                    </Text>
                    <ScrollView
                        showsVerticalScrollIndicator={false}
                        style={{
                            flexShrink: 1
                        }}
                    >
                        <View
                            style={[
                                stylesheet.formContainer,
                                {
                                    marginTop: spaces.spacingMd,
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text>{localize("students-preview-name", [
                                previewItem?.name_surname || ""
                            ])}</Text>
                            <Text>{localize("students-preview-tc", [
                                previewItem?.tc_no || ""
                            ])}</Text>
                            <Text>{localize("students-preview-age", [
                                previewItem?.age?.toString() || ""
                            ])}</Text>
                            <Text>{localize("students-preview-phone", [
                                previewItem?.phone || ""
                            ])}</Text>
                            <Text>{localize("students-preview-email", [
                                previewItem?.email || ""
                            ])}</Text>
                            <Text>{localize("students-preview-school", [
                                previewItem?.school_name || ""
                            ])}</Text>
                            <Text>{localize("students-preview-grade", [
                                previewItem?.grade_year || ""
                            ])}</Text>
                            <Text>{localize("students-preview-reading-level", [
                                previewItem?.reading_level || ""
                            ])}</Text>
                            <Text>{localize("students-preview-address", [
                                previewItem?.address || ""
                            ])}</Text>
                            <Text>{localize("students-preview-parent-name", [
                                previewItem?.parent_name_surname || ""
                            ])}</Text>
                            <Text>{localize("students-preview-parent-phone", [
                                previewItem?.parent_phone || ""
                            ])}</Text>
                            <Text>{localize("students-preview-parent-email", [
                                previewItem?.parent_email || ""
                            ])}</Text>
                            <Text>{localize("students-preview-parent-address", [
                                previewItem?.parent_address || ""
                            ])}</Text>
                            <Text>{localize("students-preview-notes", [
                                previewItem?.notes || ""
                            ])}</Text>
                        </View>
                    </ScrollView>
                    <View
                        style={[
                            stylesheet.modalActions,
                            {
                                flexDirection: isMobile ? "column-reverse" : "row",
                                marginTop: spaces.spacingXl,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            title={localize("students-preview-close")}
                            onPress={() => setPreviewItem(null)}
                            spreadBehaviour="stretch"
                            type="neutral"
                        />
                    </View>
                </View>
            </Modal>
            {pagination.totalPages > 1 && (
                <View
                    style={[
                        stylesheet.paginationContainer,
                        {
                            marginBottom: spaces.spacingXl,
                            marginTop: spaces.spacingXl,
                            gap: spaces.spacingMd,
                            flexWrap: "wrap"
                        }
                    ]}
                >
                    <Button
                        onPress={() => handlePageChange(pagination.currentPage - 1)}
                        title={localize("students-pagination-prev")}
                        isDisabled={pagination.currentPage === 1}
                        icon={ChevronLeftIcon}
                        color="secondary"
                    />
                    <Text
                        variant="bodyMediumSize"
                    >
                        {localize("students-pagination-info", [
                            pagination.currentPage.toString(),
                            pagination.totalPages.toString()
                        ])}
                    </Text>
                    <Button
                        isDisabled={pagination.currentPage === pagination.totalPages}
                        onPress={() => handlePageChange(pagination.currentPage + 1)}
                        title={localize("students-pagination-next")}
                        icon={ChevronRightIcon}
                        iconDirection="right"
                        color="secondary"
                    />
                </View>)}
        </View>
        <Modal
            onOverlayPress={({
                closeAnimation
            }) => closeAnimation(() => {
                setModalVisible(false);
                setEditId(null);
            })}
            onClosed={() => {
                setModalVisible(false);
                setEditId(null);
            }}
            portalName="modal-system"
            isActive={modalVisible}
            id="student-modal"
        >
            <View
                style={[
                    stylesheet.modalContent,
                    {
                        backgroundColor: colors.content.container.default,
                        width: isMobile ? "100%" : 800,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {editId ? localize("students-modal-edit-title") : localize("students-modal-create-title")}
                </Text>
                <ScrollView
                    showsVerticalScrollIndicator={false}
                    style={{
                        flexShrink: 1
                    }}
                >
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                marginTop: spaces.spacingMd,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.formRow,
                                {
                                    flexDirection: isMobile ? "column" : "row",
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.formColumn,
                                    {
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleMediumSize"
                                >
                                    {localize("students-modal-student-info")}
                                </Text>
                                <TextInput
                                    placeholder={localize("students-modal-tc-placeholder")}
                                    title={localize("students-modal-tc-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        tc_no: text
                                    }))}
                                    defaultValue={formData.tc_no}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("students-modal-name-placeholder")}
                                    title={localize("students-modal-name-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        name_surname: text
                                    }))}
                                    defaultValue={formData.name_surname}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("students-modal-age-placeholder")}
                                    title={localize("students-modal-age-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        age: text
                                    }))}
                                    defaultValue={formData.age}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("students-modal-phone-placeholder")}
                                    title={localize("students-modal-phone-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        phone: text
                                    }))}
                                    defaultValue={formData.phone}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("students-modal-email-placeholder")}
                                    title={localize("students-modal-email-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        email: text
                                    }))}
                                    defaultValue={formData.email}
                                    keyboardType="email-address"
                                    spreadBehaviour="stretch"
                                    autoCapitalize="none"
                                />
                                <TextInput
                                    placeholder={localize("students-modal-address-placeholder")}
                                    title={localize("students-modal-address-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        address: text
                                    }))}
                                    defaultValue={formData.address}
                                    spreadBehaviour="stretch"
                                    multiline={true}
                                />
                            </View>
                            <View
                                style={[
                                    stylesheet.formColumn,
                                    {
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleMediumSize"
                                >
                                    {localize("students-modal-school-parent-info")}
                                </Text>
                                <TextInput
                                    placeholder={localize("students-modal-school-placeholder")}
                                    title={localize("students-modal-school-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        school_name: text
                                    }))}
                                    defaultValue={formData.school_name}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("students-modal-grade-placeholder")}
                                    title={localize("students-modal-grade-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        grade_year: text
                                    }))}
                                    defaultValue={formData.grade_year}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("students-modal-reading-level-placeholder")}
                                    title={localize("students-modal-reading-level-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        reading_level: text
                                    }))}
                                    defaultValue={formData.reading_level}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("students-modal-parent-name-placeholder")}
                                    title={localize("students-modal-parent-name-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        parent_name_surname: text
                                    }))}
                                    defaultValue={formData.parent_name_surname}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("students-modal-parent-phone-placeholder")}
                                    title={localize("students-modal-parent-phone-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        parent_phone: text
                                    }))}
                                    defaultValue={formData.parent_phone}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("students-modal-parent-email-placeholder")}
                                    title={localize("students-modal-parent-email-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        parent_email: text
                                    }))}
                                    defaultValue={formData.parent_email}
                                    keyboardType="email-address"
                                    spreadBehaviour="stretch"
                                    autoCapitalize="none"
                                />
                            </View>
                        </View>
                        <TextInput
                            placeholder={localize("students-modal-parent-address-placeholder")}
                            title={localize("students-modal-parent-address-placeholder")}
                            onChangeText={(text) => setFormData(prev => ({
                                ...prev,
                                parent_address: text
                            }))}
                            defaultValue={formData.parent_address}
                            spreadBehaviour="stretch"
                            multiline={true}
                        />
                        <TextInput
                            placeholder={localize("students-modal-notes-placeholder")}
                            title={localize("students-modal-notes-placeholder")}
                            onChangeText={(text) => setFormData(prev => ({
                                ...prev,
                                notes: text
                            }))}
                            defaultValue={formData.notes}
                            spreadBehaviour="stretch"
                            multiline={true}
                        />
                    </View>
                </ScrollView>
                <View
                    style={[
                        stylesheet.modalActions,
                        {
                            flexDirection: isMobile ? "column-reverse" : "row",
                            marginTop: spaces.spacingXl,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        title={localize("students-modal-cancel")}
                        onPress={() => setModalVisible(false)}
                        spreadBehaviour="stretch"
                        type="neutral"
                    />
                    <Button
                        title={localize("students-modal-save")}
                        spreadBehaviour="stretch"
                        isLoading={isSubmitting}
                        onPress={handleSave}
                        type="primary"
                    />
                </View>
            </View>
        </Modal>
    </PageContainer>;
};

export default Students;
